import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Hash, Search, FileText, Youtube, Download, ArrowRight, Star, Users, Zap, Link2, Type, Shield, BarChart3, Eye } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ToolSEOContent from "@/components/ToolSEOContent";
import { useEffect } from "react";

const FREE_TOOLS = [
  {
    id: "hashtag-generator",
    title: "Hashtag Generator",
    description: "Generate 30+ trending hashtags for Instagram, TikTok & Twitter",
    icon: <Hash className="w-6 h-6" />,
    color: "from-pink-500 to-rose-500",
    popular: true
  },
  {
    id: "keyword-research",
    title: "Keyword Research Tool",
    description: "Find high-volume keywords with search data",
    icon: <Search className="w-6 h-6" />,
    color: "from-emerald-500 to-teal-600",
    popular: true
  },
  {
    id: "seo-audit",
    title: "SEO Audit Tool",
    description: "Analyze your website with 100+ SEO checks instantly",
    icon: <Search className="w-6 h-6" />,
    color: "from-blue-500 to-cyan-500",
    popular: true
  },
  {
    id: "backlink-checker",
    title: "Backlink Checker",
    description: "Analyze backlinks and domain authority",
    icon: <Link2 className="w-6 h-6" />,
    color: "from-blue-500 to-indigo-600",
    popular: true
  },
  {
    id: "youtube-title-generator",
    title: "YouTube Title Generator",
    description: "Create viral video titles with AI",
    icon: <Youtube className="w-6 h-6" />,
    color: "from-red-500 to-orange-500",
    popular: false
  },
  {
    id: "title-generator",
    title: "Blog Title Generator",
    description: "Generate catchy blog titles with AI",
    icon: <Type className="w-6 h-6" />,
    color: "from-orange-500 to-red-500",
    popular: false
  },
  {
    id: "meta-description-generator",
    title: "Meta Description Generator",
    description: "Write SEO-optimized meta descriptions",
    icon: <FileText className="w-6 h-6" />,
    color: "from-green-500 to-emerald-500",
    popular: false
  },
  {
    id: "video-downloader",
    title: "Video Downloader",
    description: "Download Instagram & Facebook videos free",
    icon: <Download className="w-6 h-6" />,
    color: "from-purple-500 to-violet-500",
    popular: true
  },
  {
    id: "instagram-image-downloader",
    title: "Instagram Image Downloader",
    description: "Download photos from Instagram posts instantly",
    icon: <Download className="w-6 h-6" />,
    color: "from-pink-500 to-purple-600",
    popular: true
  },
  {
    id: "plagiarism-checker",
    title: "Plagiarism Checker",
    description: "Check content for plagiarism instantly",
    icon: <Shield className="w-6 h-6" />,
    color: "from-red-500 to-pink-600",
    popular: false
  },
  {
    id: "domain-authority-checker",
    title: "Domain Authority Checker",
    description: "Check DA score of any website",
    icon: <BarChart3 className="w-6 h-6" />,
    color: "from-violet-500 to-purple-600",
    popular: false
  },
  {
    id: "readability-checker",
    title: "Readability Checker",
    description: "Analyze text readability and grade level",
    icon: <Eye className="w-6 h-6" />,
    color: "from-green-500 to-emerald-600",
    popular: false
  }
];

export default function FreeToolsDirectory() {
  useEffect(() => {
    document.title = "Free Marketing Tools | MarkeeMix - 100% Free, No Signup";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Free Marketing Tools
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Powerful SEO & social media tools. 100% free, no signup required.
          </p>
          
          {/* Stats */}
          <div className="flex items-center justify-center gap-8 mt-8 text-sm">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              <span className="font-bold text-lg">127K+</span>
              <span className="text-gray-500">Users</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              <span className="font-bold text-lg">2.5M+</span>
              <span className="text-gray-500">Searches</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-orange-500 fill-orange-500" />
              <span className="font-bold text-lg">4.9</span>
              <span className="text-gray-500">Rating</span>
            </div>
          </div>
        </div>

        {/* Tools Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {FREE_TOOLS.map((tool) => (
            <Link key={tool.id} to={`/free/${tool.id}`}>
              <Card className="p-6 h-full hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer relative overflow-hidden group">
                {tool.popular && (
                  <span className="absolute top-3 right-3 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                    Popular
                  </span>
                )}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center text-white mb-4`}>
                  {tool.icon}
                </div>
                <h3 className="font-bold text-lg mb-2 group-hover:text-purple-600 transition-colors">
                  {tool.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">{tool.description}</p>
                <div className="flex items-center text-purple-600 text-sm font-medium">
                  Try Free <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Card>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <Card className="p-8 md:p-12 text-center bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Want 75+ More Tools?
          </h2>
          <p className="text-lg opacity-90 mb-6 max-w-xl mx-auto">
            Get unlimited access to all SEO, content, social media & design tools
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing">
              <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 px-8">
                Start 7-Day Free Trial
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                View All Tools
              </Button>
            </Link>
          </div>
        </Card>
      </main>

      <ToolSEOContent toolId="hashtag-generator" />
      <Footer />
    </div>
  );
}
